import { useState } from "react";
import { useTranslation } from "react-i18next";
import { UserPlus, Edit, Save, X, Calendar, FileText, User, CheckCircle, Clock } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { useCountryConfig } from "../../hooks/useCountryConfig";
import api from "../../services/api";

import EmployeePhotoSection from "./EmployeePhotoSection";
import EmployeePersonalInfoSection from "./EmployeePersonalInfoSection";
import EmployeeContactSection from "./EmployeeContactSection";
import EmployeeJobSection from "./EmployeeJobSection";
import EmployeeContractSection from "./EmployeeContractSection";
import EmployeeBiometricsSection from "./EmployeeBiometricsSection";
import EmployeeMobileAccessSection from "./EmployeeMobileAccessSection";
import EmployeeHierarchySection from "./EmployeeHierarchySection";
import EmployeeDatesSection from "./EmployeeDatesSection";
import EmployeeGulfSection from "./EmployeeGulfSection";

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  present:  { labelKey: "employeesPage.employeeModal.attendanceStatus.present",  color: "#27a644", bg: "rgba(39,166,68,0.10)"  },
  late:     { labelKey: "employeesPage.employeeModal.attendanceStatus.late",     color: "#f0a500", bg: "rgba(240,165,0,0.10)"  },
  absent:   { labelKey: "employeesPage.employeeModal.attendanceStatus.absent",   color: "#e5484d", bg: "rgba(229,72,77,0.10)"  },
  holiday:  { labelKey: "employeesPage.employeeModal.attendanceStatus.holiday",  color: "#5e6ad2", bg: "rgba(94,106,210,0.10)" },
  weekend:  { labelKey: "employeesPage.employeeModal.attendanceStatus.weekend",  color: "#62666d", bg: "rgba(98,102,109,0.10)" },
};

const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const cfg = STATUS_CONFIG[status] ?? { label: status, color: "#8a8f98", bg: "rgba(138,143,152,0.10)" };
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ color: cfg.color, backgroundColor: cfg.bg }}>
      {cfg.labelKey ? t(cfg.labelKey) : cfg.label}
    </span>
  );
};

// ─── Attendance tab ───────────────────────────────────────────────────────────

const AttendanceTab = ({ employeeId }) => {
  const { t, i18n } = useTranslation();
  const { data, isPending } = useQuery({
    queryKey: ["employee-attendances", employeeId],
    queryFn: () => api.get(`v1/employees/${employeeId}/attendances`, { params: { limit: 30 } })
      .then(r => r.data),
    enabled: !!employeeId,
    staleTime: 60_000,
  });

  if (isPending) return (
    <div className="flex items-center justify-center h-32">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: "#5e6ad2" }} />
    </div>
  );

  const records = data?.records ?? [];

  if (!records.length) return (
    <div className="flex flex-col items-center justify-center h-32 gap-2" style={{ color: "var(--lm-ink-2)" }}>
      <Calendar className="h-8 w-8 opacity-30" />
      <p className="text-sm">{t("employeesPage.employeeModal.attendance.empty")}</p>
    </div>
  );

  return (
    <div className="space-y-3">
      <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{t("employeesPage.employeeModal.attendance.last30Days")}</p>
      <div className="rounded-lg overflow-hidden" style={{ border: "1px solid var(--lm-border)" }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ backgroundColor: "var(--lm-s2)", borderBottom: "1px solid var(--lm-border)" }}>
              <th className="text-left px-3 py-2 font-medium" style={{ color: "var(--lm-ink-3)", fontSize: "11px" }}>{t("employeesPage.employeeModal.attendance.columns.date")}</th>
              <th className="text-left px-3 py-2 font-medium" style={{ color: "var(--lm-ink-3)", fontSize: "11px" }}>{t("employeesPage.employeeModal.attendance.columns.status")}</th>
              <th className="text-left px-3 py-2 font-medium" style={{ color: "var(--lm-ink-3)", fontSize: "11px" }}>{t("employeesPage.employeeModal.attendance.columns.entry")}</th>
              <th className="text-left px-3 py-2 font-medium" style={{ color: "var(--lm-ink-3)", fontSize: "11px" }}>{t("employeesPage.employeeModal.attendance.columns.exit")}</th>
              <th className="text-right px-3 py-2 font-medium" style={{ color: "var(--lm-ink-3)", fontSize: "11px" }}>{t("employeesPage.employeeModal.attendance.columns.hours")}</th>
              <th className="text-right px-3 py-2 font-medium" style={{ color: "var(--lm-ink-3)", fontSize: "11px" }}>{t("employeesPage.employeeModal.attendance.columns.late")}</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r, i) => (
              <tr key={r.date}
                style={{
                  borderBottom: i < records.length - 1 ? "1px solid var(--lm-border)" : "none",
                  backgroundColor: i % 2 === 0 ? "transparent" : "var(--lm-s1)",
                }}>
                <td className="px-3 py-2 font-mono" style={{ color: "var(--lm-ink-2)", fontSize: "12px" }}>
                  {new Date(r.date).toLocaleDateString(i18n.language, { day: "2-digit", month: "short" })}
                </td>
                <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                <td className="px-3 py-2 font-mono" style={{ color: "var(--lm-ink)", fontSize: "12px" }}>
                  {r.actual_entry ? r.actual_entry.slice(0, 5) : "—"}
                </td>
                <td className="px-3 py-2 font-mono" style={{ color: "var(--lm-ink)", fontSize: "12px" }}>
                  {r.actual_exit ? r.actual_exit.slice(0, 5) : "—"}
                </td>
                <td className="px-3 py-2 text-right font-mono" style={{ color: "var(--lm-ink)", fontSize: "12px" }}>
                  {r.worked_hours > 0 ? `${r.worked_hours}h` : "—"}
                </td>
                <td className="px-3 py-2 text-right font-mono" style={{
                  color: r.lateness_minutes > 0 ? "#f0a500" : "var(--lm-ink-3)", fontSize: "12px"
                }}>
                  {r.lateness_minutes > 0 ? `${r.lateness_minutes}mn` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Absences tab ─────────────────────────────────────────────────────────────

const ABSENCE_LABELS = {
  paid_leave:    "employeesPage.employeeModal.absenceTypes.paidLeave",
  unpaid_leave:  "employeesPage.employeeModal.absenceTypes.unpaidLeave",
  sick_leave:    "employeesPage.employeeModal.absenceTypes.sickLeave",
  training:      "employeesPage.employeeModal.absenceTypes.training",
  business_trip: "employeesPage.employeeModal.absenceTypes.businessTrip",
  maternity:     "employeesPage.employeeModal.absenceTypes.maternity",
  paternity:     "employeesPage.employeeModal.absenceTypes.paternity",
  bereavement:   "employeesPage.employeeModal.absenceTypes.bereavement",
  other:         "employeesPage.employeeModal.absenceTypes.other",
};

const AbsencesTab = ({ employeeId }) => {
  const { t, i18n } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  const { data, isPending } = useQuery({
    queryKey: ["employee-absences", employeeId, year],
    queryFn: () => api.get(`v1/employees/${employeeId}/absences`, { params: { annee: year } })
      .then(r => r.data),
    enabled: !!employeeId,
    staleTime: 60_000,
  });

  const records = data?.records ?? [];
  const totalDays = data?.total_days ?? 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>
          {t("employeesPage.employeeModal.absences.summary", { count: records.length, totalDays })}
        </p>
        <select
          value={year}
          onChange={e => setYear(Number(e.target.value))}
          className="text-xs rounded-md px-2 py-1"
          style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}
        >
          {[currentYear, currentYear - 1, currentYear - 2].map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {isPending ? (
        <div className="flex items-center justify-center h-24">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: "#5e6ad2" }} />
        </div>
      ) : !records.length ? (
        <div className="flex flex-col items-center justify-center h-32 gap-2" style={{ color: "var(--lm-ink-2)" }}>
          <CheckCircle className="h-8 w-8 opacity-30" />
          <p className="text-sm">{t("employeesPage.employeeModal.absences.emptyYear")}</p>
        </div>
      ) : (
        <div className="rounded-lg overflow-hidden" style={{ border: "1px solid var(--lm-border)" }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: "var(--lm-s2)", borderBottom: "1px solid var(--lm-border)" }}>
                <th className="text-left px-3 py-2 font-medium" style={{ color: "var(--lm-ink-3)", fontSize: "11px" }}>{t("employeesPage.employeeModal.absences.columns.type")}</th>
                <th className="text-left px-3 py-2 font-medium" style={{ color: "var(--lm-ink-3)", fontSize: "11px" }}>{t("employeesPage.employeeModal.absences.columns.start")}</th>
                <th className="text-left px-3 py-2 font-medium" style={{ color: "var(--lm-ink-3)", fontSize: "11px" }}>{t("employeesPage.employeeModal.absences.columns.end")}</th>
                <th className="text-right px-3 py-2 font-medium" style={{ color: "var(--lm-ink-3)", fontSize: "11px" }}>{t("employeesPage.employeeModal.absences.columns.days")}</th>
              </tr>
            </thead>
            <tbody>
              {records.map((a, i) => (
                <tr key={a.id}
                  style={{
                    borderBottom: i < records.length - 1 ? "1px solid var(--lm-border)" : "none",
                    backgroundColor: i % 2 === 0 ? "transparent" : "var(--lm-s1)",
                  }}>
                  <td className="px-3 py-2" style={{ color: "var(--lm-ink)", fontSize: "12px" }}>
                    {ABSENCE_LABELS[a.type] ? t(ABSENCE_LABELS[a.type]) : a.type}
                  </td>
                  <td className="px-3 py-2 font-mono" style={{ color: "var(--lm-ink-2)", fontSize: "12px" }}>
                    {new Date(a.start_date).toLocaleDateString(i18n.language)}
                  </td>
                  <td className="px-3 py-2 font-mono" style={{ color: "var(--lm-ink-2)", fontSize: "12px" }}>
                    {new Date(a.end_date).toLocaleDateString(i18n.language)}
                  </td>
                  <td className="px-3 py-2 text-right font-medium" style={{ color: "var(--lm-ink)", fontSize: "12px" }}>
                    {a.days_count}j
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Main modal ───────────────────────────────────────────────────────────────

const EmployeeFormModal = ({
  open, onOpenChange, mode, formData, updateField,
  photoPreview, selectedEmployee, uploadingPhoto, handlePhotoUpload,
  departements, sites, employees, loading, handleSubmit, onCancel,
}) => {
  const { t } = useTranslation();
  const { config } = useCountryConfig();
  const [activeTab, setActiveTab] = useState("info");
  const isEdit = mode === "edit";

  const countryCode = config?.code ?? "TN";
  const phonePlaceholder = config?.phone?.placeholder ?? "+216 XX XXX XXX";

  const formSections = (
    <div className="space-y-6">
      <EmployeePhotoSection
        photoPreview={photoPreview}
        selectedEmployee={selectedEmployee}
        uploadingPhoto={uploadingPhoto}
        handlePhotoUpload={handlePhotoUpload}
      />
      <EmployeePersonalInfoSection formData={formData} updateField={updateField} />
      <EmployeeContactSection formData={formData} updateField={updateField} phonePlaceholder={phonePlaceholder} />
      <EmployeeJobSection formData={formData} updateField={updateField} departements={departements} />
      <EmployeeContractSection formData={formData} updateField={updateField} sites={sites} />
      <EmployeeGulfSection formData={formData} updateField={updateField} countryCode={countryCode} />
      <EmployeeBiometricsSection formData={formData} updateField={updateField} />
      {isEdit && <EmployeeMobileAccessSection employee={selectedEmployee} />}
      <EmployeeHierarchySection formData={formData} updateField={updateField} employees={employees} />
      <EmployeeDatesSection formData={formData} updateField={updateField} />
    </div>
  );

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-7xl max-h-[95vh] overflow-y-auto overflow-x-auto">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center space-x-2">
            {isEdit ? <Edit className="h-5 w-5" /> : <UserPlus className="h-5 w-5" />}
            <span>{isEdit ? t("employees.editEmployee") : t("employees.addEmployee")}</span>
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isEdit
              ? t("employees.employeeDetails")
              : t("employeesPage.dialogs.addEmployeeDesc")}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {isEdit ? (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="info" className="flex items-center gap-2">
                <User className="h-3.5 w-3.5" />
                {t('employees.tabs.info')}
              </TabsTrigger>
              <TabsTrigger value="presence" className="flex items-center gap-2">
                <Clock className="h-3.5 w-3.5" />
                {t('employees.tabs.attendance')}
              </TabsTrigger>
              <TabsTrigger value="absences" className="flex items-center gap-2">
                <FileText className="h-3.5 w-3.5" />
                {t('employees.tabs.absences')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="info">{formSections}</TabsContent>
            <TabsContent value="presence">
              <AttendanceTab employeeId={selectedEmployee?.id} />
            </TabsContent>
            <TabsContent value="absences">
              <AbsencesTab employeeId={selectedEmployee?.id} />
            </TabsContent>
          </Tabs>
        ) : (
          formSections
        )}

        <AlertDialogFooter className="flex justify-between">
          <AlertDialogCancel onClick={onCancel}>
            <X className="mr-2 h-4 w-4" />
            {t("common.cancel")}
          </AlertDialogCancel>
          {(activeTab === "info" || !isEdit) && (
            <AlertDialogAction onClick={handleSubmit} disabled={loading}>
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {isEdit ? t("employees.editEmployee") : t("employees.addEmployee")}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default EmployeeFormModal;
